export const SERVICE_FORM_TEXTS = {
  // Steps
  STEPS: {
    STEP_1: 'User permissions',
    STEP_2: 'Offering overview',
    STEP_3: 'Offering details',
    STEP_4: 'Capabilities & use cases',
    STEP_5: 'Summary',
  },

  // Page headers
  HEADERS: {
    ADD_NEW: 'Add new offering',
    EDIT: 'Edit',
    OFFERING_TYPE: 'Professional service',
  },

  // Step 1: User permissions
  PERMISSIONS: {
    OWNER: {
      SECTION_TITLE: 'Content Owner Information',
      LABEL: "Content owner's email address",
      PLACEHOLDER: 'e.g. email.address@roche.com',
      HINT: 'Enter the email address of the content owner. The content owner should be the person accountable for this offering, such as a Product Manager or a Product Line Lead.',
    },
    DEPUTY: {
      SECTION_TITLE: 'Deputy Content Owner Information',
      LABEL: "Deputy content owner's email address",
      PLACEHOLDER: 'e.g. email.address@roche.com',
      HINT: 'Enter the email address of the deputy content owner. The deputy content owner serves as the backup accountable person for this offering. This role can be filled by anyone on the team.',
    },
    AUTHOR: {
      SECTION_TITLE: 'Content Author Information',
      LABEL: "Content author's email address",
      PLACEHOLDER: 'e.g. email.one@roche.com',
      HINT: 'Enter the email address of the content author. The content author is responsible for creating and maintaining content for this offering. Multiple email addresses should be separated by commas.',
    },
  },

  // Step 2: Offering overview
  OVERVIEW: {
    GENERAL_INFO: {
      SECTION_TITLE: 'General Information',
      DISPLAY_NAME: {
        LABEL: 'Offering name',
        PLACEHOLDER: 'e.g. Gmail',
        HINT: 'Enter the offering display name. This is the name that users will see in Panorama.',
      },
      APPROVAL_STATUS: {
        LABEL: 'Approval status',
        PLACEHOLDER: 'Select approval status for your offering',
        HINT: 'Select approval status that is most appropriate for your offering.',
      },
      SUCCESSOR: {
        LABEL: 'Successor (optional)',
        PLACEHOLDER: 'Select successor for your offering',
        HINT: 'Select the approved offering that will be used to replace this offering.',
      },
    },
    DESCRIPTION: {
      SECTION_TITLE: 'Offering Description',
      LABEL: 'Offering description',
      PLACEHOLDER: 'e.g. Simplifying Workflows and Driving Collaboration',
      HINT: 'Enter a high level description of the purpose and value of the offering (25 words or less).',
    },
    SIMILAR_OFFERINGS: {
      SECTION_TITLE: 'Similar Offerings In Panorama (optional)',
      LABEL: 'Similar offerings in Panorama (optional)',
      PLACEHOLDER: 'Select similar offerings for your offering',
      HINT: 'Select other offerings with similar capabilities. Similar offerings will be associated with your offering. Only offerings approved in Panorama can be selected.',
    },
  },

  // Step 3: Offering details
  DETAILS: {
    ONBOARDING: {
      SECTION_TITLE: 'Engagement information',
      LABEL: 'Engagement steps',
      HINT: 'Add step-by-step sequential actions required to engage the service.',
      ACCESS_INSTRUCTIONS: {
        LABEL: 'Engagement instructions',
        PLACEHOLDER: 'Enter engagement instructions...',
      },
      TABLE_COLUMNS: {
        STEP_DESCRIPTION: 'Step description',
        ACCESS_URL: 'Access URL',
      },
      CTA: 'Add new Engagement Step',
    },
    ADDITIONAL_COST: {
      SECTION_TITLE: 'Additional Cost Considerations (optional)',
      ADDITIONAL_COST_CONSIDERATIONS: {
        CHECKBOX_LABEL: 'Additional cost considerations available',
        LABEL: 'Additional cost considerations',
        PLACEHOLDER: 'Enter additional cost considerations',
        HINT: 'Enter details for additional costs (e.g. development costs).',
      },
      OTHER_PRICING_MODELS: {
        CHECKBOX_LABEL: 'Other pricing models available',
        LABEL: 'Other pricing models',
        PLACEHOLDER: 'Enter other available pricing models details',
        HINT: 'Enter other available pricing models (e.g., Cost per 10 users).',
      },
    },
    AI_AGENT: {
      SECTION_TITLE: 'AI Agent (optional)',
      HINT: 'Add a list of internal AI agents, such as Lumin, associated with this offering.',
      TABLE_COLUMNS: {
        NAME: 'AI Agent name',
        URL: 'AI Agent URL',
      },
      CTA: 'Add new AI Agent',
    },
    COMMUNITY: {
      SECTION_TITLE: 'Community (optional)',
      HINT: 'Add information about the user communities or collaborative spaces associated with the offering.',
      TABLE_COLUMNS: {
        TITLE: 'Community name',
        DESCRIPTION: 'Community description',
        URL: 'Community URL',
      },
      CTA: 'Add new Community',
    },
    SERVICE_AND_REQUEST: {
      SECTION_TITLE: 'Services & Requests',
      HINT: 'Add request channels available for this offering, such as consultancy, support, incident, or general requests.',
      TABLE_COLUMNS: {
        ID: 'Request ID',
        TYPE: 'Request type',
        TITLE: 'Request title',
        DESCRIPTION: 'Instructions for users',
        DESTINATION: 'Destination URL',
      },
      CTA: 'Add new Service & Request',
    },
    VENDOR: {
      SECTION_TITLE: 'Vendor information (optional)',
      NAME: {
        LABEL: 'Vendor name',
        PLACEHOLDER: 'e.g. Salesforce',
        HINT: 'Enter the name of the vendor or supplier.',
      },
      SUPPORT: {
        LABEL: 'Supported services (optional)',
        PLACEHOLDER:
          'Enter the details of the support services contracted with the vendor',
        HINT: 'Add a list of the support services contracted with vendor.',
      },
      URL: {
        LABEL: 'Vendor URL (optional)',
        HINT: "Add a direct link to the vendor's primary website or support information.",
      },
    },
    RELATED_TECHNOLOGY: {
      SECTION_TITLE: 'Related Technology (optional)',
      HINT: 'Add a collection of references to other published offerings in the Panorama catalog that are used to provide the service.',
      TABLE_COLUMNS: {
        NAME: 'Related technology name',
        DESCRIPTION: 'Related technology description',
      },
      CTA: 'Add new Related Technology',
    },
    ADDONS: {
      SECTION_TITLE: 'Available Add-ons (optional)',
      CHECKBOX_LABEL: 'Add-ons available',
      CHECKBOX_HINT:
        'Indicate whether approved add-ons are available for your offering.',
      TABLE_LABEL: 'Add-ons details',
      TABLE_HINT:
        'Add a list of approved plugins, integrations, or extensions available for your offering.',
      TABLE_COLUMNS: {
        NAME: 'Add-on name',
        DESCRIPTION: 'Description',
        COST: 'Cost information',
        SUPPORT: 'Support availability',
        URL: 'Add-on URL',
      },
      CTA: 'Add new Add-on',
    },
    EXTERNAL_USE: {
      SECTION_TITLE: 'External use',
      LABEL: 'Allowance for external use',
      PLACEHOLDER: 'Select the option for external use allowance',
      HINT: 'Indicate whether solutions built or hosted on this platform are allowed for use by external third parties or outside the primary Roche/Genentech organization.',
    },
  },

  // Step 4: Features and use cases
  FUNCTIONALITY: {
    CAPABILITIES: {
      SECTION_TITLE: 'Major Capabilities',
      HINT: 'Add core capabilities that describe broad abilities associated with the offering.',
      TABLE_COLUMNS: {
        CAPABILITY_NAME: 'Capability name',
        CAPABILITY_DESCRIPTION: 'Capability description',
        CAPABILITY_BENEFIT: 'Capability benefit',
      },
      CTA: 'Add new Major Capability',
    },
    FEATURES: {
      SECTION_TITLE: 'Features',
      HINT: 'Add a list of the major functionalities or capabilities of your offering. Include the feature name, a brief explanation of its purpose, and the advantages or value it provides.',
      TABLE_COLUMNS: {
        FEATURE_NAME: 'Feature name',
        FEATURE_DESCRIPTION: 'Feature description',
        FEATURE_BENEFIT: 'Feature benefit',
      },
      CTA: 'Add new feature',
    },
    USE_CASES: {
      SECTION_TITLE: 'Use cases (optional)',
      HINT: 'Add a set of typical scenarios or contexts in which the offering is used. Include the use case name, a brief description of how the offering is utilized, and by whom.',
      TABLE_COLUMNS: {
        USE_CASE_NAME: 'Use case name',
        USE_CASE_DESCRIPTION: 'Use case description',
      },
      CTA: 'Add new use case',
    },
  },
};

// Export individual sections for easier imports if needed
export const STEP_LABELS = SERVICE_FORM_TEXTS.STEPS;
